import waitForElementTransition from "wait-for-element-transition";
import { stripHtml } from "string-strip-html";
import createI18n from "./i18n";
import { readonly, ref } from "vue";

const ApiUrl =
    (process.env.VUE_APP_API_DOMAIN ?? window.location.origin) +
    process.env.VUE_APP_API_URI;

/** Get fetch promise to bridge api */
const fetchApiBridge = async (query, callback) => {
    let url = new URL("CenshareBridge", ApiUrl);
    if (typeof query !== "undefined") {
        url.search = "?" + query;
    }
    fetch(url.href, {
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((res) => res.json())
        .then(callback);
};

/** Get fetch promise to favorite api */
const fetchApiFavorites = async (
    query,
    callback,
    postAction = false,
    deleteAction = false
) => {
    let req = {
        headers: {
            "Content-Type": "application/json",
        },
    };
    if (postAction) {
        req.method = "POST";
        req.body = JSON.stringify(postAction);
    }

    if (deleteAction) {
        req.method = "DELETE";
        req.body = JSON.stringify(deleteAction);
    }

    let url = new URL(query, ApiUrl + "favorites/");
    fetch(url.href, req)
        .then((res) => res.json())
        .then(callback);
};

const favorites = ref([]);
const favoritesLoaded = ref(false);
const favCount = ref(0);
const userHmac = readonly(window.userHmac);

const getFavCount = () => {
    if (!favorites.value.length) {
        favCount.value = 0;
    } else {
        let query = [
            "(" +
            '(assetType="component.factsheet."&factsheetText.*.language="' +
            i18n.locale.value +
            '")' +
            "|" +
            '(assetType="component.webinar-on-demand."&webinarText.*.language="' +
            i18n.locale.value +
            '")' +
            ")",
        ];

        query.push('(assetId="' + favorites.value.join('"|assetId="') + '")');

        query = query.length ? query.join("&") : "";

        if (query !== "") {
            query = "&query=" + encodeURIComponent(query);
        }

        fetchApiBridge(
            "limit=-1&p=" + process.env.VUE_APP_API_ENTRY + query,
            (res) => {
                if (typeof res.result !== "undefined") {
                    favCount.value = res.result.length;
                }
            }
        );
    }
};

if (typeof userHmac !== "undefined") {
    fetchApiFavorites(userHmac, (res) => {
        let list = [];
        res.forEach((i) => {
            if (i.asset_id) {
                list.push(i.asset_id);
            }
        });
        favorites.value = list;
        favoritesLoaded.value = true;

        if (favorites.value.length) {
            getFavCount();
        }
    });
}

/** Pointer to i18n instance */
const i18n = createI18n.global;

const createPagination = (page, limit) => {
    return {
        page: page,
        pageCount: 0,
        limit: limit,
        itemCount: 0,
    };
};

const getSession = () => {
    let session = sessionStorage.getItem("FsData");
    if (session === null) {
        session = {
            language: i18n.locale.value,
            limit: 20,
            page: {
                list: 1,
                products: 1,
                favorites: 1,
            },
            assetIds: [],
            activeFilters: {
                category: "",
                industries: [],
                services: [],
                countries: [],
                regions: [],
                ingredients: [],
                applications: [],
            },
        };
        setSession(session);
    } else {
        session = JSON.parse(session);
    }
    return session;
};

const setSession = (session) => {
    sessionStorage.setItem("FsData", JSON.stringify(session));
};

const setPage = (layout, pagination, session, value) => {
    pagination.value.page = value;
    session.page[layout] = value;
    setSession(session);
};

const setLimit = (layout, pagination, session, value) => {
    pagination.value.page = 1;
    session.page[layout] = 1;
    pagination.value.limit = value;
    session.limit = value;
    setSession(session);
};

const setFileType = (layout, activeFilters, session, value) => {
    activeFilters.value.fileType = value;
    session.activeFilters.fileType = value;
    setSession(session);
};

const setIndustries = (layout, pagination, activeFilters, session, value) => {
    activeFilters.value.industries = value;
    session.activeFilters.industries = value;
    setPage(layout, pagination, session, 1);
};

const setIngredients = (layout, pagination, activeFilters, session, value) => {
    activeFilters.value.ingredients = value;
    session.activeFilters.ingredients = value;
    setPage(layout, pagination, session, 1);
};

const setApplications = (layout, pagination, activeFilters, session, value) => {
    activeFilters.value.applications = value;
    session.activeFilters.applications = value;
    setPage(layout, pagination, session, 1);
};

const setServices = (layout, pagination, activeFilters, session, value) => {
    activeFilters.value.services = value;
    session.activeFilters.services = value;
    setPage(layout, pagination, session, 1);
};

const setRegions = (layout, pagination, activeFilters, session, value) => {
    activeFilters.value.regions = value;
    session.activeFilters.regions = value;
    setPage(layout, pagination, session, 1);
};

const setCategory = (layout, pagination, activeFilters, session, value) => {
    activeFilters.value.category = value;
    session.activeFilters.category = value;
    setPage(layout, pagination, session, 1);
};

const getLocalizedName = (item) => {
    return item.localizedName
        ? item.localizedName[i18n.locale.value] ??
              item.localizedName[i18n.fallbackLocale.value] ??
              ""
        : "";
};

const getText = (item, ns) => {
    if (item.assetType) {
        switch (item.assetType) {
            case "component.factsheet.":
                return item.factsheetText &&
                item.factsheetText[i18n.locale.value] &&
                item.factsheetText[i18n.locale.value][ns]
                    ? stripHtml(item.factsheetText[i18n.locale.value][ns]).result
                    : "";

            case "component.webinar-on-demand.":
                return item.webinarText &&
                item.webinarText[i18n.locale.value] &&
                item.webinarText[i18n.locale.value][ns]
                    ? stripHtml(item.webinarText[i18n.locale.value][ns]).result
                    : "";
        }
    }
    return "";
};

const createItemOption = (item) => {
    let val = "";
    if (typeof item.assetId !== "undefined") {
        val = item.assetId;
    } else if (typeof item.key !== "undefined") {
        val = item.key;
    }
    return {
        label: getLocalizedName(item),
        value: val,
    };
};

const isTouchDevice = () => {
    return (
        true ==
        ("ontouchstart" in window ||
            (window.DocumentTouch && document instanceof DocumentTouch))
    );
};

const checkEntityPath = (url) => {
    let path = url.split("/entity/");
    if (path.length !== 2) {
        return false;
    }
    return path[1];
};

window.cardImages = {};

const getImageObject = async (item) => {
    if (typeof item.mainPicture === "undefined") {
        return {
            card: null,
            modal: null,
        };
    } else {
        let path = checkEntityPath(item.mainPicture);
        if (!path) {
            return {
                card: null,
                modal: null,
            };
        } else {
            if (typeof window.cardImages[path] !== "undefined") {
                return window.cardImages[path];
            } else {
                await fetchApiBridge("p=" + path, (res) => {
                    window.cardImages[path] = {
                        card: checkEntityPath(res.variants["400x250"].link),
                        modal: checkEntityPath(res.variants["800x500"].link),
                    };
                });
                return window.cardImages[path];
            }
        }
    }
};

const simplifyName = (name) => {
    return name
        .toLowerCase()
        .replace(/([^0-9a-z\s])/g, "")
        .replace(/ +/g, "_");
};

const activeFilterHelper = (activeFilters, filters, dataLayer) => {
    activeFilters.forEach((id) => {
        filters.find((f) => {
            if (f.id === id) {
                dataLayer.push(simplifyName(f.name));
                return true;
            }
            return false;
        });
    });
};

const cardFilterHelper = (activeFilters, filters, dataLayer) => {
    activeFilters.forEach((url) => {
        let id = parseInt(url.split("/").pop());
        filters.find((f) => {
            if (f.id === id) {
                dataLayer.push(simplifyName(f.name));
                return true;
            }
            return false;
        });
    });
};

const dataLayerPush = (card, filters, activeFilters) => {
    if (
        typeof window.dataLayer === "object" &&
        typeof window.dataLayer.length !== "undefined"
    ) {
        let o = {
            data: {
                markets: [],
                product_applications: [],
                products: [],
            },
            event: "factsheet_downloaded",
            item: simplifyName(card.name),
        };

        let filterAction = false;
        if (
            typeof activeFilters.industries !== "undefined" &&
            activeFilters.industries.length > 0
        ) {
            filterAction = true;
            activeFilterHelper(
                activeFilters.industries,
                filters.industries,
                o.data.markets
            );
        }

        if (
            typeof activeFilters.applications !== "undefined" &&
            activeFilters.applications.length > 0
        ) {
            filterAction = true;
            activeFilterHelper(
                activeFilters.applications,
                filters.applications,
                o.data.product_applications
            );
        }

        if (
            typeof activeFilters.ingredients !== "undefined" &&
            activeFilters.ingredients.length > 0
        ) {
            filterAction = true;
            activeFilterHelper(
                activeFilters.ingredients,
                filters.ingredients,
                o.data.products
            );
        }

        if (!filterAction) {
            if (
                typeof card.industries !== "undefined" &&
                card.industries.length > 0
            ) {
                cardFilterHelper(
                    card.industries,
                    filters.industries,
                    o.data.markets
                );
            }

            if (
                typeof card.applications !== "undefined" &&
                card.applications.length > 0
            ) {
                cardFilterHelper(
                    card.applications,
                    filters.applications,
                    o.data.product_applications
                );
            }

            if (
                typeof card.ingredients !== "undefined" &&
                card.ingredients.length > 0
            ) {
                cardFilterHelper(
                    card.ingredients,
                    filters.ingredients,
                    o.data.products
                );
            }
        }
        window.dataLayer.push(o);
    }
};

const dropDownClasses = () => {
    return {
        open: "open",
        close: "close",
        opened: "opened",
    };
};

const transitionClassesOpen = (opt) => {
    if (typeof opt.classes === "undefined") {
        opt.classes = {};
    }
    opt.classes = Object.assign(dropDownClasses(), opt.classes);
    opt.el.classList.remove(opt.classes.opened);
    opt.el.classList.remove(opt.classes.close);
    opt.el.classList.add(opt.classes.open);

    if (opt.dropDown) {
        opt.dropDown.style.height =
            opt.dropDown.children[0].offsetHeight + "px";
    }
    if (opt.dropDown) {
        waitForElementTransition(opt.dropDown).then(() => {
            opt.el.classList.add(opt.classes.opened);
            opt.el.classList.remove(opt.classes.open);
            opt.dropDown.style.height = null;
        });
    } else {
        waitForElementTransition(opt.el).then(() => {
            opt.el.classList.add(opt.classes.opened);
            opt.el.classList.remove(opt.classes.open);
        });
    }
};

const transitionClassesClose = (opt) => {
    if (typeof opt.classes === "undefined") {
        opt.classes = {};
    }
    opt.classes = Object.assign(dropDownClasses(), opt.classes);
    if (opt.dropDown) {
        opt.dropDown.style.height =
            opt.dropDown.children[0].offsetHeight + "px";
    }
    opt.el.classList.remove(opt.classes.opened);
    opt.el.classList.remove(opt.classes.open);
    opt.el.classList.add(opt.classes.close);
    if (opt.dropDown) {
        window.setTimeout(() => {
            opt.dropDown.style.height = null;
            waitForElementTransition(opt.dropDown).then(() => {
                opt.el.classList.remove(opt.classes.close);
            });
        });
    } else {
        waitForElementTransition(opt.el).then(() => {
            opt.el.classList.remove(opt.classes.close);
        });
    }
};

const transitionClassesToggle = (opt) => {
    if (typeof opt.classes === "undefined") {
        opt.classes = {};
    }
    opt.classes = Object.assign(dropDownClasses(), opt.classes);
    if (opt.el.classList.contains(opt.classes.opened)) {
        transitionClassesClose(opt);
    } else {
        transitionClassesOpen(opt);
    }
};

export {
    waitForElementTransition,
    ApiUrl,
    i18n,
    fetchApiBridge,
    fetchApiFavorites,
    createPagination,
    getSession,
    setSession,
    setPage,
    setLimit,
    setFileType,
    setIndustries,
    setIngredients,
    setApplications,
    setServices,
    setRegions,
    setCategory,
    createItemOption,
    getLocalizedName,
    getText,
    isTouchDevice,
    checkEntityPath,
    getImageObject,
    dataLayerPush,
    dropDownClasses,
    transitionClassesOpen,
    transitionClassesClose,
    transitionClassesToggle,
    getFavCount,
    userHmac,
    favorites,
    favoritesLoaded,
    favCount,
};
